import React from 'react'
import { Skeleton, Text, csx } from '@vtex/admin-ui'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import {
  getPrioritizedPurchaseConditionsPrices,
  normalizeProductPrice,
  getCheapestPurchaseConditionPrice,
} from '../../utils/product'
import { PurchaseConditionPrice } from '../../typings/product'

type ProductPriceContentProps = {
  purchaseConditionsPrices: PurchaseConditionPrice[]
  currencyCode: string
}

const ProductPriceContent = ({
  purchaseConditionsPrices,
  currencyCode,
}: ProductPriceContentProps) => {
  const prioritizedProductPurchaseConditionsPrices = getPrioritizedPurchaseConditionsPrices(
    purchaseConditionsPrices
  )

  const isProductAvailable = !!prioritizedProductPurchaseConditionsPrices.length

  if (!isProductAvailable) {
    return (
      <Text tone="secondary">
        <FormattedMessage id="searchResultUnavailable" />
      </Text>
    )
  }

  const hasOnlyOneConditionPrice =
    prioritizedProductPurchaseConditionsPrices.length === 1

  if (hasOnlyOneConditionPrice) {
    const price = normalizeProductPrice(
      prioritizedProductPurchaseConditionsPrices[0].price
    )

    const listPrice = normalizeProductPrice(
      prioritizedProductPurchaseConditionsPrices[0].listPrice
    )

    const shouldShowListPrice = listPrice > price

    return (
      <>
        {shouldShowListPrice && (
          <Text
            variant="action2"
            tone="secondary"
            className={csx({
              display: 'inline-block',
              textDecoration: 'line-through',
              marginRight: '$space-3',
            })}
          >
            <FormattedNumber
              currency={currencyCode}
              value={listPrice}
              style="currency"
            />
          </Text>
        )}
        <Text
          variant="action2"
          tone="secondary"
          className={csx({ display: 'inline-block' })}
        >
          <FormattedNumber
            currency={currencyCode}
            value={price}
            style="currency"
          />
        </Text>
      </>
    )
  }

  // Multiple prices

  const cheapestConditionPrice = getCheapestPurchaseConditionPrice(
    prioritizedProductPurchaseConditionsPrices
  )

  const normalizedPrice = normalizeProductPrice(cheapestConditionPrice?.price)

  return (
    <>
      <Text
        variant="action2"
        tone="secondary"
        className={csx({ display: 'inline-block' })}
      >
        <FormattedMessage id="price-starting-from" />
        <FormattedNumber
          value={normalizedPrice}
          currency={currencyCode}
          style="currency"
        />
      </Text>
    </>
  )
}

export type ProductPriceProps = {
  purchaseConditionsPrices: PurchaseConditionPrice[]
  isBusy: boolean
  currencyCode: string
}

export const ProductPrice = ({
  purchaseConditionsPrices,
  isBusy,
  currencyCode,
}: ProductPriceProps) => (
  <div className={csx({ display: 'flex', marginTop: '$space-3' })}>
    {isBusy ? (
      <Skeleton
        className={csx({
          display: 'inline-block',
          width: '50%',
          height: '1rem',
        })}
      />
    ) : (
      <ProductPriceContent
        purchaseConditionsPrices={purchaseConditionsPrices}
        currencyCode={currencyCode}
      />
    )}
  </div>
)
